import React, {useEffect, useMemo} from "react";
import DashboardLayout from "../../layouts/Dashboard";
import {PageContainer, PageHeader} from "../../components";
import {useForm, useWatch} from "react-hook-form";
import {FormProvider} from "../../components/hook-form";
import {Button, Grid, FormControlLabel, Switch} from "@mui/material";
import RHFTextField from "../../components/hook-form/RHFTextField";
import {
    ServiceCategorySelect,
    ServiceGroupSelect,
    ServiceKindSelect,
    ServiceSubGroupSelect,
    UnitOfMeasure,
    DepartmentSelect,
    DivisionSelect
} from "../../components/Select/domainSelects";
import LoadingButton from "@mui/lab/LoadingButton";
import {serviceSchema} from "../../models/schemas/serviceSchema";
import {yupResolver} from "@hookform/resolvers/yup";
import {serviceCRUD, servicePriceListCRUD} from "../../http";
import {useHistory, useParams} from "react-router-dom";
import {SERVICE_LIST_CLIENT_ROUTE, SERVICE_LIST_ROUTE} from "../../routes";
import {renderControls} from "../../functions";
import useAlert from "../../hooks/useAlert";
import {uuid} from "@tinymce/tinymce-react/lib/es2015/main/ts/Utils";
import { v4 as uuidv4 } from "uuid";
import { SERVICE_TYPE_GOVERNMENT, SERVICE_TYPE_PAYED } from "../../redux/constants/serviceTypeConstants";

const toNullableNumber = (v) => {
    if (v === "" || v === undefined) return null;
    if (v === null) return null;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : null;
};

const normalizeSchemaValue = (v) => {
    if (v === undefined) return undefined;
    if (v === null) return null;
    if (typeof v === "string") return v;
    try {
        return JSON.stringify(v);
    } catch (e) {
        return null;
    }
};

const sanitizeServicePayload = (raw) => {
    // Backend часто падает на вложенных объектах — оставляем только скаляры,
    // а схемы нормализуем в строку (если вдруг пришли объектами).
    const allowedSchemaKeys = new Set(["referralSchema", "probeSchema", "resultSchema"]);
    const out = {};
    Object.entries(raw || {}).forEach(([k, v]) => {
        if (allowedSchemaKeys.has(k)) {
            out[k] = normalizeSchemaValue(v);
            return;
        }
        if (v === null || v === undefined) {
            out[k] = v;
            return;
        }
        const t = typeof v;
        if (t === "string" || t === "number" || t === "boolean") {
            out[k] = v;
        }
    });
    return out;
};

const ServiceAdd = () => {
    const defaultValues = React.useMemo(() => ({
        unitId: '',
        name: '',
        code: '',
        groupId: '',
        subGroupId: '',
        categoryId: '',
        kindId: '',
        typeId: 2,
        price: 0,
        departmentId: '',
        divisionId: '',
        serviceType: SERVICE_TYPE_GOVERNMENT, // 1 - Гос. заказ, 2 - Платные
        createRequestWithoutAct: false, // Создание заявки без акта/направления НЦЭ
        // скрытые/системные поля - сохраняем при редактировании, но не отображаем
        referralSchema: null,
        probeSchema: null,
        resultSchema: null,
        parentId: null,
        filialId: null,
    }), []);

    const {id} = useParams();
    const {push} = useHistory()
    const isEditing = useMemo(() => id, [id]);
    const alert = useAlert(false, 1);

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(serviceSchema)
    });

    const {
        handleSubmit,
        setValue,
        control,
        reset,
        watch,
        formState: {isSubmitting},
    } = methods;

    useEffect(() => {
        (async function () {
            if (isEditing) {
                const res = await serviceCRUD.get(id);
                // Важно: НЕ делать reset({...res}) — там могут быть вложенные объекты,
                // которые потом улетают обратно на бек и приводят к 500.
                reset({
                    name: res?.name ?? "",
                    code: res?.code ?? "",
                    price: res?.price ?? 0,
                    unitId: res?.unitId ?? "",
                    groupId: res?.groupId ?? "",
                    subGroupId: res?.subGroupId ?? "",
                    categoryId: res?.categoryId ?? "",
                    kindId: res?.kindId ?? "",
                    departmentId: res?.departmentId ?? "",
                    divisionId: res?.divisionId ?? "",
                    serviceType: res?.serviceType ?? SERVICE_TYPE_GOVERNMENT,
                    createRequestWithoutAct: !!res?.createRequestWithoutAct,
                    referralSchema: res?.referralSchema ?? null,
                    probeSchema: res?.probeSchema ?? null,
                    resultSchema: res?.resultSchema ?? null,
                    parentId: res?.parentId ?? null,
                    filialId: res?.filialId ?? null,
                });

            }
        }())
    }, [isEditing])

    const onSubmit = async state => {
        try {
            if (isEditing) {
                const payload = sanitizeServicePayload({
                    ...state,
                    id: toNullableNumber(id) ?? id,
                    typeId: 2,
                    // нормализуем id-поля, которые часто приходят строкой из select
                    unitId: toNullableNumber(state.unitId),
                    groupId: toNullableNumber(state.groupId),
                    subGroupId: toNullableNumber(state.subGroupId),
                    categoryId: toNullableNumber(state.categoryId),
                    kindId: toNullableNumber(state.kindId),
                    departmentId: toNullableNumber(state.departmentId),
                    divisionId: toNullableNumber(state.divisionId),
                    parentId: toNullableNumber(state.parentId),
                    filialId: toNullableNumber(state.filialId),
                    price: state.price === "" ? 0 : Number(state.price),
                    serviceType: toNullableNumber(state.serviceType),
                });

                await serviceCRUD.edit(payload);
                alert.show(1, "Изменения сохранены");
            } else {
                const createPayload = sanitizeServicePayload({
                    ...state,
                    typeId: 2,
                    unitId: toNullableNumber(state.unitId),
                    groupId: toNullableNumber(state.groupId),
                    subGroupId: toNullableNumber(state.subGroupId),
                    categoryId: toNullableNumber(state.categoryId),
                    kindId: toNullableNumber(state.kindId),
                    departmentId: toNullableNumber(state.departmentId),
                    divisionId: toNullableNumber(state.divisionId),
                    parentId: toNullableNumber(state.parentId),
                    filialId: toNullableNumber(state.filialId),
                    price: state.price === "" ? 0 : Number(state.price),
                    serviceType: toNullableNumber(state.serviceType),
                });

                const {id: serviceId} = await serviceCRUD.create(createPayload);

                // Автоматическое создание записи в ServicePriceList
                await servicePriceListCRUD.create({
                    name: uuidv4(),
                    price: createPayload.price ?? 0,
                    costCoefficient: 0,
                    serviceId: serviceId,
                    typeId: createPayload.serviceType ?? SERVICE_TYPE_GOVERNMENT,
                });
                alert.show(1, "Услуга добавлена");
                push(SERVICE_LIST_ROUTE);
            }
        } catch (e) {
            // parseResponse() уже превращает axios error в строку, но на всякий случай
            const msg = typeof e === "string" ? e : (e?.message || "Ошибка сохранения");
            alert.show(0, msg);
        }
    }

    const unitId = useWatch({
        control,
        name: "unitId",
    });

    const groupId = useWatch({
        control,
        name: "groupId",
    });

    const subGroupId = useWatch({
        control,
        name: "subGroupId",
    });

    const categoryId = useWatch({
        control,
        name: "categoryId",
    });

    const kindId = useWatch({
        control,
        name: "kindId",
    });

    const departmentId = useWatch({
        control,
        name: "departmentId",
    });

    const serviceType = useWatch({
        control,
        name: "serviceType",
    });

    const createRequestWithoutAct = useWatch({
        control,
        name: "createRequestWithoutAct",
    });

    return (
        <DashboardLayout>
            <PageHeader controls={renderControls([    {
                state: "created",
                roles: [],
                buttons: [
                    <Button
                        variant={"text"}
                        onClick={() => push(SERVICE_LIST_ROUTE)}
                    >
                        Вернуться
                    </Button>
                ],
            }], {
                state: 'created',
                roles: [],
            })}  heading={isEditing ? `Услуга #${id}` : "Услуга: Создание"}>
            </PageHeader>

            <PageContainer>
                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                            <p style={{width: "50%"}}>
                                Код услуги
                            </p>
                            <RHFTextField label="Код услуги" name="code"/>
                        </Grid>
                        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                            <p style={{width: "50%"}}>
                                Полное наименование услуги
                            </p>
                            <RHFTextField label="Полное наименование" name="name"/>
                        </Grid>
                        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                            <p style={{width: "50%"}}>
                                Цена
                            </p>
                            <RHFTextField 
                                label="Цена" 
                                name="price" 
                                type="number"
                                inputProps={{ step: "0.01", min: "0" }}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                            <p style={{width: "50%"}}>
                                Ед. измерения
                            </p>
                            <UnitOfMeasure
                                fullWidth
                                value={unitId}
                                onChange={(val) => setValue('unitId', val)}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                            <p style={{width: "50%"}}>
                                Лаборатория
                            </p>
                            <DepartmentSelect
                                fullWidth
                                value={departmentId}
                                onChange={(val) => setValue('departmentId', val)}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                            <p style={{width: "50%"}}>
                                Отдел лабораторий
                            </p>
                            <DivisionSelect
                                fullWidth
                                value={watch('divisionId')}
                                departmentId={departmentId}
                                onChange={(val) => setValue('divisionId', val)}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                            <p style={{width: "50%"}}>
                                Группа
                            </p>
                            <ServiceGroupSelect
                                fullWidth
                                value={groupId}
                                onChange={(val) => setValue('groupId', val)}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                            <p style={{width: "50%"}}>
                                Подгруппа
                            </p>
                            <ServiceSubGroupSelect
                                fullWidth
                                value={subGroupId}
                                onChange={(val) => setValue('subGroupId', val)}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                            <p style={{width: "50%"}}>
                                Метод
                            </p>
                            <ServiceKindSelect
                                fullWidth
                                value={kindId}
                                onChange={(val) => setValue('kindId', val)}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                            <p style={{width: "50%"}}>
                                Категория
                            </p>
                            <ServiceCategorySelect
                                fullWidth
                                value={categoryId}
                                onChange={(val) => setValue('categoryId', val)}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                            <p style={{width: "50%"}}>
                                Тип
                            </p>
                            <select
                                value={serviceType}
                                onChange={(e) => setValue('serviceType', parseInt(e.target.value))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    fontSize: '16px'
                                }}
                            >
                                <option value={SERVICE_TYPE_GOVERNMENT}>Гос. заказ</option>
                                <option value={SERVICE_TYPE_PAYED}>Платные</option>
                            </select>
                        </Grid>
                        {serviceType === SERVICE_TYPE_GOVERNMENT && (
                            <Grid item xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                                <p style={{width: "50%"}}>
                                    Создание заявки без акта/направления НЦЭ
                                </p>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={createRequestWithoutAct}
                                            onChange={(e) => setValue('createRequestWithoutAct', e.target.checked)}
                                        />
                                    }
                                    label={createRequestWithoutAct ? "Вкл" : "Выкл"}
                                />
                            </Grid>
                        )}
                    </Grid>
                    <LoadingButton loading={isSubmitting} type="submit" variant="contained"
                                   sx={{mt: 3, mb: 2, alignSelf: 'flex-end'}}>
                        {isEditing ? "Сохранить" : 'Добавить'}
                    </LoadingButton>
                </FormProvider>
            </PageContainer>
            {alert.render()}
        </DashboardLayout>
    )
};

export default ServiceAdd;
